import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { UploadOutlined, CheckOutlined } from "@ant-design/icons";
import {
  Flex,
  Button,
  Input,
  Col,
  Row,
  Select,
  Space,
  Upload,
  notification,
  Form,
} from "antd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMandatoryFeilds,
  updateMandatoryFields,
  createClient,
} from "../../../services/adminController";

interface MappingItem {
  key: string;
  value: string;
}

interface BankDetails {
  accountNumber: string;
  bankHolderName: string;
  bankName: string;
  branchName: string;
  branchAddress: string;
  ifsc: string;
}

interface FormData {
  clientName: string;
  companyName: string;
  email: string;
  mobileNumber: string;
  address: string;
  bankDetails: BankDetails;
}

const initialFormData: FormData = {
  clientName: "",
  companyName: "",
  email: "",
  mobileNumber: "",
  address: "",
  bankDetails: {
    accountNumber: "",
    bankHolderName: "",
    bankName: "",
    branchName: "",
    branchAddress: "",
    ifsc: "",
  },
};

export const CreateTable: React.FC = ({}) => {
  const [states, setStates] = useState<{
    extractedList: [];
    nbfcName: string;
    selectedFields: any[];
    loader: boolean;
    message: string;
  }>({
    extractedList: [],
    nbfcName: "",
    selectedFields: [],
    loader: false,
    message: "",
  });

  const [formData, setFormData] = useState({
    address: "",
    bankDetails: {
      accountNumber: "",
      bankHolderName: "",
      bankName: "",
      branchAddress: "",
      branchName: "",
      ifsc: "",
    },
    clientName: "",
    companyName: "",
    email: "",
    mobileNumber: "",
  });

  const { data: mandatoryFields, isPending } = useQuery({
    queryKey: ["mandatory_fields"],
    queryFn: () => getMandatoryFeilds(),
  });
  const formItemStyle: React.CSSProperties = {
    marginBottom: "12px",
    marginTop: "20px",
  };

const renderFormItem = (
  label: string,
  name: string,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  type: string = "text",
  required?: boolean,
  helperText?: string
) => {
  const isMobileField = name === "mobileNumber";

  return (
    <Row style={formItemStyle} align="middle">
      <Col
        span={12}
        style={{
          paddingRight: "12px",
          whiteSpace: "nowrap",
        }}
      >
        <label>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}:
        </label>
      </Col>
      <Col span={12}>
        <Input
          name={name}
          value={value}
          onChange={onChange}
          type={type}
          maxLength={isMobileField ? 10 : undefined}
        />
        {helperText && (
          <div style={{ fontSize: "10px", color: "#888", marginTop: "4px" }}>
            {helperText}
          </div>
        )}
      </Col>
    </Row>
  );
};

const handleFileUpload = (event: any) => {
  //console.log("#>>> handleFileUpload() ==> event :: ", event);
  const isXLSX =
    event.type ===
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  //console.log("#>>> isXSLX :: ", isXLSX);
  if (!isXLSX) {
    notification.error({
      message: "Error",
      description: "File format not supported. Please upload an XLSX file.",
    });
    return;
  }

  const reader = new FileReader();

  reader.onload = (e) => {
    // Read the file as an array buffer
    const data = e.target!.result;
    const workbook = XLSX.read(data, { type: "array" });

    // Get the first sheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert sheet to JSON (array of rows)
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    // Extract the first row
    const firstRowData: any = jsonData[0];
    //console.log("#>>> handleFileUpload() => firstRowData :: ", firstRowData);
    setStates((prev) => ({ ...prev, extractedList: firstRowData }));
  };

  // Read the file as an array buffer for xlsx to process
  reader.readAsArrayBuffer(event);
};

const onClear = (selectedValIndex: number) => {
  //console.log('#>> onClear() :: selectedValIndex :: ', selectedValIndex);
  const tempSelectedFields = states.selectedFields;
  //console.log("#>> onClear() :: tempSelectedFields :0: ", tempSelectedFields);
  const deleteItem = tempSelectedFields.findIndex(
    (item: any) => item.key === states.extractedList[selectedValIndex]
  );
  //console.log('#>> onClear() :: deleteItem :: ', deleteItem);
  tempSelectedFields.splice(deleteItem, 1);
  //console.log("#>> onClear() :: tempSelectedFields :1: ", tempSelectedFields);
  setStates((prev) => ({ ...prev, selectedFields: tempSelectedFields }));
};

const RenderList: React.FC<{ items: string[]; index: number }> = useCallback(
  ({ items, index }) => {
    return (
      <Flex className="mt-4">
        <Col span={6}>
          <p>{items}:</p>
        </Col>
        <Col span={6}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Select Fields"}
            allowClear
            onClear={() => onClear(index)}
            value={states.selectedFields.find(
              (item: any) => item.key === items
            )}
            onSelect={(e, options) => {
              if (Boolean(states.selectedFields[index])) {
                onClear(index);
              }
              const selectedFieldsIndex = states.selectedFields.indexOf(items);
              if (selectedFieldsIndex === -1) {
                setStates((prev) => ({
                  ...prev,
                  selectedFields: [
                    ...prev.selectedFields,
                    { key: items, value: options.value },
                  ],
                }));
              }
            }}
            options={mandatoryFields?.data?.data?.requiredFields
              ?.filter(
                (ele: any) =>
                  !states.selectedFields.some(
                    (field: any) => field.value === ele?.name
                  )
              )
              ?.map((item: any) => ({
                key: item.name,
                value: item.name,
                label: item.name,
                mandatory: item.required,
              }))}
            optionRender={(option: any) => {
              return (
                <Space>
                  <span>{option.label}</span>
                  {Boolean(option?.data?.mandatory) && (
                    <span style={{ color: "red" }}>{`*`}</span>
                  )}
                </Space>
              );
            }}
          />
        </Col>
      </Flex>
    );
  },
  [isPending, states.selectedFields]
);

const updateClientList = async () => {
  const res = await createClient(formData);
  //console.log('#>> updateClientList() :: res : ', JSON.stringify(res));
  if (res.status === 200) {
    // notification.success({ message: "Success", description: res.data?.data });
    setStates((prev) => ({ ...prev, message: res?.data?.message }));
    const result = createMapping(states.selectedFields, states.extractedList);
    const payload = {
      clientId: res.data?.data?.clientId,
      mapping: result.mapping,
    };
    //console.log("#>> payload :: ", JSON.stringify(payload));
    updateMandatoryFieldsHandler(payload);
  } else {
    notification.error({ message: "Error", description: res.data?.message });
  }
};

const { mutate: updateMandatoryFieldsHandler } = useMutation({
  mutationFn: updateMandatoryFields,
  mutationKey: ["updateMandatoryFields"],
  onSuccess: (res, variables, context) => {
    if (res.status === 200) {
      // console.log("#>> updateMandatoryFieldsHandler >> res :: ", res);
      // queryClient.invalidateQueries({ queryKey: ["users"] });
      // queryClient.invalidateQueries({ queryKey: ["managers"] });

      notification.success({
        message: "Success",
        description: states.message,
      });
      setFormData(initialFormData);
      setStates((prev) => ({
        ...prev,
        extractedList: [],
        nbfcName: "",
        selectedFields: [],
        message: "",
        loader: false,
      }));
    }
  },
  onError: (error: any) => {
    console.error("#>> updateMandatoryFieldsHandler >> Error :: ", error);
    notification.error({
      message: "Error",
      description: error.response.data.message,
    });
    setStates((prev) => ({ ...prev, message: "", loader: false }));
  },
});

function createMapping(
  selectedArray: MappingItem[],
  totalKeys: string[]
): { mapping: Record<string, string>[] } {
  const mappingObj: Record<string, string> = {};

  selectedArray.forEach((item) => {
    mappingObj[item.key] = item.value;
  });

  const result: Record<string, string> = {};

  totalKeys.forEach((key) => {
    if (mappingObj[key]) {
      result[key] = mappingObj[key];
    } else {
      result[key] = "N/A";
    }
  });

  totalKeys.forEach((key) => {
    if (!(key in result)) {
      result[key] = "N/A";
    }
  });

  return {
    mapping: [result],
  };
}

const validateData = () => {
  setStates((prev) => ({ ...prev, loader: true }));
  // if (states.nbfcName.length <= 0) {
  //   notification.error({
  //     message: "Error",
  //     description: "NBFC name is required.",
  //   });
  //   setStates((prev) => ({ ...prev, loader: false }));
  //   return;
  // }
  if (formData.clientName.trim().length === 0) {
    notification.error({
      message: "Error",
      description: "Client Name is required.",
    });
    setStates((prev) => ({ ...prev, loader: false }));

    return;
  }

  if (formData.companyName.trim().length === 0) {
    notification.error({
      message: "Error",
      description: "Company Name is required.",
    });
    setStates((prev) => ({ ...prev, loader: false }));

    return;
  }

  if (
    states.selectedFields.length <
    mandatoryFields?.data?.data?.requiredFields?.filter(
      (item: any) => item.required === true
    )?.length
  ) {
    notification.error({
      message: "Error",
      description: "Please select all mandatory fields.",
    });
    setStates((prev) => ({ ...prev, loader: false }));
    return;
  }

  updateClientList();
};

const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;

  if (name in formData.bankDetails) {
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value,
      },
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }
};

return (
  <div>
    <Row className="flex gap-3" gutter={32}>
      <Col>
        <p>
          <b>
            <u>Enter Lender Fields</u>
          </b>
        </p>
        {renderFormItem(
          "Company Name",
          "companyName",
          formData.companyName,
          handleChange,
          "text",
          true,
          "This will be used in legal notices and communications."
        )}
        {renderFormItem(
          "Lender Name",
          "clientName",
          formData.clientName,
          handleChange,
          "text",
          true
        )}

        {renderFormItem(
          "Mobile Number",
          "mobileNumber",
          formData.mobileNumber,
          handleChange,
          "text"
        )}
        {renderFormItem(
          "Address",
          "address",
          formData.address,
          handleChange,
          "text"
        )}
        {renderFormItem(
          "Email",
          "email",
          formData.email,
          handleChange,
          "email"
        )}
      </Col>
      <Col>
        <p>
          <b>
            <u>Enter Lender Bank Details</u>
          </b>
        </p>
        {renderFormItem(
          "Account Holder Name",
          "bankHolderName",
          formData.bankDetails.bankHolderName,
          handleChange
        )}
        {renderFormItem(
          "Account Number",
          "accountNumber",
          formData.bankDetails.accountNumber,
          handleChange
        )}

        {renderFormItem(
          "Bank Name",
          "bankName",
          formData.bankDetails.bankName,
          handleChange
        )}
        {renderFormItem(
          "IFSC Code",
          "ifsc",
          formData.bankDetails.ifsc,
          handleChange
        )}
        {renderFormItem(
          "Branch Name",
          "branchName",
          formData.bankDetails.branchName,
          handleChange
        )}
        {renderFormItem(
          "Branch Address",
          "branchAddress",
          formData.bankDetails.branchAddress,
          handleChange
        )}

        {/* <Input
            placeholder="Please enter Lender name"
            value={states.nbfcName}
            onChange={(e) =>
              setStates((prev) => ({ ...prev, nbfcName: e.target.value }))
            }
          /> */}
      </Col>
      <Upload
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.oasis.opendocument.spreadsheet, .xls, .xlsx"
        maxCount={1}
        showUploadList={false}
        beforeUpload={handleFileUpload}
        onRemove={() => setStates((prev) => ({ ...prev, extractedList: [] }))}
      >
        {states.extractedList.length > 0 ? (
          <Button icon={<CheckOutlined />}>Change File</Button>
        ) : (
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        )}
      </Upload>
    </Row>

    {!isPending && states.extractedList.length > 0 && (
      <div className="mt-4">
        <p>
          <b>
            <u>Select Mandatory Fields</u>
          </b>
        </p>
        {states.extractedList.map((item: any, index: number) => (
          <RenderList key={index.toString()} index={index} items={item} />
        ))}
        <div
          style={{
            marginTop: "50px",
            alignItems: "center",
            justifyItems: "center",
          }}
        >
          <Button
            type="primary"
            style={{ marginLeft: "200px" }}
            className="pl-10 pr-10"
            loading={states.loader}
            onClick={validateData}
          >
            Submit
          </Button>
        </div>
      </div>
    )}
  </div>
);
};
